import { useState } from "react";
import {
  Search,
  Video,
  MoreVertical,
  Calendar,
  Clock,
  Play,
  Copy,
  Edit,
  Link2,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/admin/ui/Button";
import { Input } from "@/components/admin/ui/Input";
import { Badge } from "@/components/admin/ui/Badge";
import { useToast } from "@/components/admin/ui/Toast";
import { zoomMeetings } from "@/data/adminMock";

export function Zoom() {
  const [meetingsList, setMeetingsList] = useState(zoomMeetings);
  const [activeTab, setActiveTab] = useState<
    "all" | "live" | "upcoming" | "expired"
  >("all");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<number[]>([]);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const { showToast } = useToast();

  const tabs: { key: typeof activeTab; label: string }[] = [
    { key: "all", label: "All" },
    { key: "live", label: "Live" },
    { key: "upcoming", label: "Upcoming" },
    { key: "expired", label: "Expired" },
  ];

  const filteredMeetings = meetingsList.filter((m) => {
    const matchesTab = activeTab === "all" ? true : m.status.toLowerCase() === activeTab;
    const matchesSearch =
      m.title.toLowerCase().includes(search.toLowerCase()) ||
      m.course.toLowerCase().includes(search.toLowerCase()) ||
      m.meetingId.toLowerCase().includes(search.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const toggleAll = () => {
    if (selected.length === filteredMeetings.length) {
      setSelected([]);
    } else {
      setSelected(filteredMeetings.map((m) => m.id));
    }
  };

  const toggleOne = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleCopy = (meetingId: string) => {
    navigator.clipboard.writeText(meetingId).then(() => {
      showToast(`Copied Meeting ID: ${meetingId}`, "success");
    });
  };

  const handleJoin = (meeting: typeof zoomMeetings[0]) => {
    if (meeting.status === "Expired") return;
    if (meeting.joinUrl) {
      window.open(meeting.joinUrl, "_blank");
    } else {
      showToast("Join URL not available", "warning");
    }
  };

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Zoom
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage live classes and meetings
          </p>
        </div>
        <Button leftIcon={<Video className="h-4 w-4" />}>
          Create a Zoom Meeting
        </Button>
      </div>

      {/* Table Card */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden transition-colors">
        {/* Tabs */}
        <div className="px-5 pt-4 border-b border-gray-100">
          <div className="flex items-center gap-6">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`relative pb-3 text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? "text-[#ff0000]"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
                {activeTab === tab.key && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#ff0000] rounded-full" />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-100 flex items-center justify-between gap-4">
          <Input
            placeholder="Search Meeting..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search className="h-4 w-4" />}
            containerClassName="w-full sm:w-80"
          />
          {selected.length > 0 && (
            <span className="text-xs text-gray-500 font-medium">
              {selected.length} selected
            </span>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="px-5 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={
                      selected.length === filteredMeetings.length &&
                      filteredMeetings.length > 0
                    }
                    onChange={toggleAll}
                    className="rounded border-gray-300 text-[#ff0000] focus:ring-[#ff0000]/30"
                  />
                </th>
                <th className="px-5 py-3 font-medium">Meeting Title / Info</th>
                <th className="px-5 py-3 font-medium">ID</th>
                <th className="px-5 py-3 font-medium">Course</th>
                <th className="px-5 py-3 font-medium">Time / Date</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filteredMeetings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-gray-500">
                    No meetings found matching the criteria.
                  </td>
                </tr>
              ) : (
                filteredMeetings.map((meeting) => (
                  <tr
                    key={meeting.id}
                    className="hover:bg-gray-50/60 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <input
                        type="checkbox"
                        checked={selected.includes(meeting.id)}
                        onChange={() => toggleOne(meeting.id)}
                        className="rounded border-gray-300 text-[#ff0000] focus:ring-[#ff0000]/30"
                      />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3 min-w-[260px]">
                        <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                          <Video className="h-5 w-5 text-[#ff0000]" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">
                            {meeting.title}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Zoom Cloud Meeting
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2 text-gray-700">
                        <span>{meeting.meetingId}</span>
                        <button
                          type="button"
                          onClick={() => handleCopy(meeting.meetingId)}
                          className="text-gray-400 hover:text-[#ff0000] transition-colors"
                          title="Copy Meeting ID"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      {meeting.course}
                    </td>
                    <td className="px-5 py-4">
                      <div className="text-gray-700 text-sm">
                        <p className="flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5 text-gray-400" />{" "}
                          {meeting.date}
                        </p>
                        <p className="flex items-center gap-1.5 mt-0.5 text-gray-500">
                          <Clock className="h-3.5 w-3.5 text-gray-400" />{" "}
                          {meeting.time}
                        </p>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <Badge
                        variant={
                          meeting.status === "Live"
                            ? "green"
                            : meeting.status === "Upcoming"
                            ? "blue"
                            : "gray"
                        }
                      >
                        {meeting.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          leftIcon={<Play className="h-4 w-4" />}
                          disabled={meeting.status === "Expired"}
                          onClick={() => handleJoin(meeting)}
                        >
                          Join
                        </Button>
                        <div className="relative">
                          <button
                            onClick={() => setOpenMenuId(openMenuId === meeting.id ? null : meeting.id)}
                            className="p-1.5 text-[#94A3B8] hover:text-[#374151] hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {openMenuId === meeting.id && (
                            <>
                              <div className="fixed inset-0 z-40" onClick={() => setOpenMenuId(null)} />
                              <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-50 py-1 animate-in fade-in slide-in-from-top-1 duration-150">
                                <button
                                  onClick={() => { setOpenMenuId(null); showToast("Opening meeting editor...", "info"); }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#374151] hover:bg-gray-50 transition-colors"
                                >
                                  <Edit className="h-3.5 w-3.5" /> Edit Meeting
                                </button>
                                <button
                                  onClick={() => {
                                    if (meeting.joinUrl) {
                                      navigator.clipboard.writeText(meeting.joinUrl);
                                      showToast("Join link copied!", "success");
                                    }
                                    setOpenMenuId(null);
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#374151] hover:bg-gray-50 transition-colors"
                                >
                                  <Link2 className="h-3.5 w-3.5" /> Copy Join Link
                                </button>
                                <hr className="my-1 border-gray-100" />
                                <button
                                  onClick={() => {
                                    setMeetingsList(prev => prev.filter(m => m.id !== meeting.id));
                                    setOpenMenuId(null);
                                    showToast("Meeting deleted.", "error");
                                  }}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 className="h-3.5 w-3.5" /> Delete Meeting
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
